import type { Platform } from "@/stores/chat-store";
import { DEFAULT_PREFERRED_HOST, type PreferredHost } from "@spectree/shared";

// ============================================================================
// CLI guidance — host-aware (dual-host) command-string layer.
//
// SpecTree supports two co-equal CLI hosts — GitHub Copilot CLI and Claude Code
// — with host-transparent UX (migration A8 + A14). This module is the single
// source of truth for the literal command strings + agent-invocation builders
// the Sprout chat assistant, the pipeline cards, and the product pages render.
//
// The HOST_GUIDANCE[host] map below holds the per-host launch/label/explanation/
// verify strings + command builders. Every builder + accessor takes an optional
// `host` (default DEFAULT_PREFERRED_HOST = copilot) so callers that have resolved
// the user's preferred host (via `useMe()` + `resolvePreferredHost`) emit the
// matching command set, while callers not yet host-threaded (A8b: the Help
// Center wizard + some product-page prose) keep compiling and render the default
// host's commands coherently until A8b threads `host` through.
//
// NOTE (A8b — pending prose sweep): the CLI-setup step file
// (`components/onboarding/steps/cli-setup-steps.tsx`) is now host-threaded via
// useResolvedHost(). A handful of other product-page labels/headings still
// carry hardcoded "GitHub Copilot" sibling prose and consume the
// host-defaulted consts/builders below. The wizard-only data near the bottom of
// this file (PREREQUISITES, getTokenCommand, getConfigPath, the GitHub-Packages
// setup keys, launchCopilotCLI, QUICK_START_PATHS) is left intact so those
// legacy wizard screens (outside the CLI-setup flow) stay internally coherent
// until A8b migrates them wholesale.
// ============================================================================

// ---------------------------------------------------------------------------
// Quick-start setup commands (host-independent unless noted)
// ---------------------------------------------------------------------------

/**
 * Single source of truth for the production SpecTree API base URL emitted into
 * user-facing MCP registration commands and config blocks (the Claude
 * `claude mcp add --env API_BASE_URL=...` flag and the Copilot
 * `mcp-config.json` `env.API_BASE_URL` field). Keeping both flows pointed at
 * this one constant prevents the two literal copies from drifting apart.
 */
export const SPECTREE_API_BASE_URL = "https://spectree-api.toro.com";

/**
 * Host-invariant skill-pack install command, deliberately omitting `--host`.
 *
 * A bare `install @spectree/full` triggers the CLI dual-provision branch
 * (`packages/cli/src/commands/install.ts:31-36`), which installs
 * `@spectree/full` into `.github/` AND `@spectree/claude` into `.claude/`.
 * Passing any `--host` flag suppresses that branch: `--host copilot` would
 * leave a Claude Code user with an empty `.claude/`, and `--host claude`
 * would install Copilot-authored content into the Claude layout. The
 * instructions->rules remap (`HostTarget.bucketDir()`) is keyed on the
 * *pack manifest's* host and applied at bundle time
 * (`packages/cli/src/utils/pack-bundler.ts`), not on the install target:
 * install only strips the host-root prefix (`stripRootPrefix`) and preserves
 * the bucket segment verbatim. So `@spectree/full`, published as a copilot
 * pack, lands in `.claude/instructions/` rather than `.claude/rules/`.
 * Omitting `--host` is therefore correct for every host, which is why
 * {@link getInstallSkillPacks} returns the identical string regardless of the
 * `host` argument.
 *
 * Built from {@link SPECTREE_API_BASE_URL} rather than a literal URL so the
 * two copies cannot drift.
 */
export const INSTALL_SKILL_PACKS_COMMAND =
  `npx @ttc-spectree/spectree-cli install @spectree/full --registry ${SPECTREE_API_BASE_URL}`;

/** Stand-in shown in copyable config blocks before the user supplies a real token. */
export const MCP_TOKEN_PLACEHOLDER = "st_PASTE_YOUR_TOKEN_HERE";

/**
 * Copilot-host MCP config block. The MCP server de-stutters tool names by default
 * (yields Claude's idiomatic `mcp__spectree__<tool>`), but the GitHub Copilot CLI
 * surfaces MCP tools by their registered name verbatim, and all `.github/` agents +
 * instructions call the legacy `spectree__<tool>` names. `SPECTREE_TOOL_PREFIX` makes
 * the same server binary re-emit those legacy names so Copilot tool calls keep working.
 * The Claude flow (`claude mcp add`) deliberately omits this — Claude wants bare names.
 *
 * Lives here rather than in the onboarding component so it sits next to
 * {@link SPECTREE_API_BASE_URL} and {@link QUICK_START_SETUP_COMMANDS}: both host
 * flows are now pure string generation with no React dependency.
 */
export const getMcpConfigBlock = (token?: string) => {
  // Blank-not-just-nullish: the onboarding step passes `apiToken.trim()`, which is ""
  // before the user pastes anything. `??` would let that through and emit
  // `API_TOKEN: ""`, so the placeholder would never appear in the block users copy.
  const apiToken = token?.trim() ? token : MCP_TOKEN_PLACEHOLDER;

  return JSON.stringify(
    {
      mcpServers: {
        spectree: {
          command: "npx",
          // `-y` matters here: the Copilot CLI starts this server non-interactively,
          // so an install-confirmation prompt from npx would hang startup rather than
          // ask anyone. Mirrors the `npx -y` used by the Claude command and the docs.
          args: ["-y", "@ttc-spectree/spectree-mcp"],
          env: {
            API_BASE_URL: SPECTREE_API_BASE_URL,
            API_TOKEN: apiToken,
            SPECTREE_TOOL_PREFIX: "spectree__",
          },
        },
      },
    },
    null,
    2
  );
};

export const QUICK_START_SETUP_COMMANDS = {
  // --- GitHub Packages access (host-independent: needed to pull the
  //     @ttc-spectree/spectree-cli + skill packs). A8b/A7-owned. ---
  refreshPackagesAccess: "gh auth refresh -s read:packages",
  configureGithubPackages: "npm config set @ttc-spectree:registry https://npm.pkg.github.com",
  configureGithubPackagesToken: "npm config set //npm.pkg.github.com/:_authToken $(gh auth token)",

  // --- Claude Code host setup (host-specific; the Copilot host configures its
  //     MCP server through a config file rather than a `claude mcp add`). ---
  /** Install the Claude Code host binary (macOS/Linux/WSL). */
  installClaudeCode: "curl -fsSL https://claude.ai/install.sh | bash",
  /**
   * Register the SpecTree MCP server as a local-scoped stdio server: the entry
   * is stored per project in the user's own `~/.claude.json`, so the API token
   * never lands in a committable `.mcp.json`.
   */
  configureMcpServer:
    `claude mcp add --transport stdio --scope local spectree --env API_TOKEN=st_your_token --env API_BASE_URL=${SPECTREE_API_BASE_URL} -- npx -y @ttc-spectree/spectree-mcp`,

  /**
   * @deprecated Use {@link HOST_GUIDANCE}[host].launchCommand. Claude-host
   * default kept for back-compat with not-yet-host-threaded consumers (A8b).
   */
  launchAgentCLI: "claude",
  /**
   * @deprecated Use {@link HOST_GUIDANCE}[host].verifyConnection. Claude-host
   * default kept for back-compat with not-yet-host-threaded consumers (A8b).
   */
  verifyConnection: "/mcp",
  /**
   * @deprecated Copilot launch flag — see {@link HOST_GUIDANCE}.copilot. Retained
   * only for the legacy Help Center wizard
   * (`components/onboarding/steps/verification-step.tsx`) until A8b.
   */
  launchCopilotCLI: "copilot --allow-all-tools",
} as const;

export const QUICK_START_SETUP_NOTES = {
  configureGithubPackagesToken:
    "The token command below works in bash, zsh, and PowerShell. If you're using Windows Command Prompt, use for /f %i in ('gh auth token') do npm config set //npm.pkg.github.com/:_authToken %i for that step.",
} as const;

/**
 * Claude Code plugin onboarding (PI-SPEC-SW-1239). SpecTree's agents, skills,
 * workflow, and guard hooks ship as the `spectree` plugin from the
 * `TTC-SpecTree/SpecTree` marketplace. Committing this snippet to a repo's
 * `.claude/settings.json` gives every contributor the plugin (with marketplace
 * auto-update on); `/spectree:spectree-setup` then points the `spectree` MCP
 * entry at the token-fallback launcher. The raw `claude mcp add --scope local`
 * command (`QUICK_START_SETUP_COMMANDS.configureMcpServer`) stays as the
 * fallback and as the way to store the API token on first setup.
 */
export const CLAUDE_PLUGIN_SETTINGS_SNIPPET = JSON.stringify(
  {
    extraKnownMarketplaces: {
      spectree: {
        source: { source: "github", repo: "TTC-SpecTree/SpecTree" },
        autoUpdate: true,
      },
    },
    enabledPlugins: { "spectree@spectree": true },
  },
  null,
  2,
);

/** Plugin skill that installs the MCP launcher and (re)registers the `spectree` entry. */
export const CLAUDE_PLUGIN_SETUP_COMMAND = "/spectree:spectree-setup";

/** User-scope install commands, the alternative to committing the settings snippet. */
export const CLAUDE_PLUGIN_INSTALL_COMMANDS = [
  "claude plugin marketplace add TTC-SpecTree/SpecTree --scope user",
  "claude plugin install spectree@spectree --scope user",
] as const;

/**
 * GitHub Copilot host MCP config path (the Copilot analog of Claude's
 * local-scoped `~/.claude.json` entry written by `QUICK_START_SETUP_COMMANDS.configureMcpServer`).
 * Consumed by the Copilot-host onboarding walkthrough + the legacy Help Center
 * wizard / ChatProvider fallbacks (A8b).
 */
export const QUICK_START_PATHS = {
  copilotConfig: "~/.config/github-copilot/config.json",
} as const;

// ---------------------------------------------------------------------------
// Host-aware command guidance — the HOST_GUIDANCE[host] seam
// ---------------------------------------------------------------------------
//
// Slugs (A2): under the Claude host every SpecTree agent and skill ships in the
// `spectree` Claude Code plugin, so Claude names are plugin-namespaced:
// agents are `@agent-spectree:<agent>` mentions (planner/orchestrator run as
// delegated subagents via `@agent-spectree:planner` / `@agent-spectree:orchestrator`)
// and skills are `/spectree:<skill>` commands. Interview-driven flows MUST run on the main
// thread (S5/A2 §5 — their AskUserQuestion interview fabricates answers when
// delegated). Under the Copilot host every agent is a plain `@<name>` mention.
//
// Main-thread invocation form matters, and the two options are NOT equivalent
// in this UI. `claude --agent <slug>` is a SHELL command: it only runs on the
// main thread when typed at a bash/zsh prompt to start a new session. Every
// copy box in the product tells the user to paste into the Claude Code prompt
// (see `promptExplanation`), and pasting `claude --agent ...` there makes the
// host delegate it as a subagent — reintroducing exactly the fabrication bug
// the `--agent` form was chosen to avoid. Observed on FogBugz 16888
// (2026-08-04): ER-1024 was authored with zero user decisions that way.
//
// Therefore any interview flow surfaced through a copy box MUST be an
// in-session slash command, matching the `/spectree:spectree-epic-execute` precedent in
// `buildOrchestrator` below. `buildBugToErBridge` emits `/spectree:bug-to-er`, and
// `buildRequestFormulator`/`buildImproveScore` emit `/spectree:request-formulator ...`
// (the plugin's `skills/request-formulator` skill, which runs the interview
// inline in the user's current session). Do NOT reintroduce `claude --agent`.
// ---------------------------------------------------------------------------

function normalizeAgentArgument(value: string): string {
  return value
    .replace(/\r\n|\r|\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeWorkflowArgument(value: string): string {
  const normalized = normalizeAgentArgument(value);
  if (
    (normalized.startsWith('"') && normalized.endsWith('"')) ||
    (normalized.startsWith("'") && normalized.endsWith("'"))
  ) {
    return normalized.slice(1, -1).trim();
  }

  return normalized;
}

function quoteAgentArgument(value: string): string {
  return JSON.stringify(normalizeAgentArgument(value));
}

/** A single account/license prerequisite shown by the onboarding wizard. */
export interface HostPrerequisite {
  id: string;
  label: string;
  description: string;
}

/** Per-host command strings + agent-invocation builders. */
export interface HostGuidance {
  /** Display name of the host CLI (for prose/labels). */
  hostLabel: string;
  /** Label for the copyable launch/command box shown across the product. */
  cliLabel: string;
  /** Explains where a generated agent command should be pasted. */
  promptExplanation: string;
  /** Launch the interactive host session. */
  launchCommand: string;
  /** In-session verification that the SpecTree tools are connected. */
  verifyConnection: string;
  /** Install the SpecTree skill packs for this host. */
  installSkillPacks: string;
  /**
   * Account/license prerequisites the onboarding wizard asks the user to obtain
   * before setup. Copilot needs a GitHub Copilot License; Claude needs a Claude
   * account (browser sign-in). Both still need GitHub Enterprise access to pull
   * SpecTree's private repos + skill packs from GitHub Packages.
   */
  prerequisites: readonly HostPrerequisite[];
  buildRequestFormulator: (idea: string) => string;
  buildImproveScore: (identifier: string) => string;
  buildImproveEpic: (identifier: string) => string;
  buildPlanner: (description: string) => string;
  buildPlannerFromRequest: (title: string) => string;
  buildOrchestrator: (epicIdentifier: string) => string;
  buildOrchestratorFeature: (identifier: string) => string;
  buildBugToErBridge: (caseId: number) => string;
}

export const HOST_GUIDANCE: Record<PreferredHost, HostGuidance> = {
  copilot: {
    hostLabel: "GitHub Copilot CLI",
    cliLabel: "Run in GitHub Copilot CLI (Terminal)",
    promptExplanation:
      "Paste this into GitHub Copilot chat or the Copilot CLI prompt — not into a normal bash or zsh prompt.",
    launchCommand: "copilot --allow-all-tools",
    verifyConnection: "@spectree list epics",
    installSkillPacks: INSTALL_SKILL_PACKS_COMMAND,
    prerequisites: [
      {
        id: "copilot-license",
        label: "GitHub Copilot License",
        description: "Required for AI agent integration. Request through MyTech.",
      },
      {
        id: "enterprise-access",
        label: "GitHub Enterprise Access",
        description: "Required to access SpecTree repositories. Request through MyTech.",
      },
    ],
    buildRequestFormulator: (idea) => `@request-formulator ${quoteAgentArgument(idea)}`,
    buildImproveScore: (identifier) => `@request-formulator improve ${quoteAgentArgument(identifier)}`,
    buildImproveEpic: (identifier) => `@planner improve ${quoteAgentArgument(identifier)}`,
    buildPlanner: (description) => `@planner ${quoteAgentArgument(description)}`,
    buildPlannerFromRequest: (title) => `@planner --from-request ${quoteAgentArgument(title)}`,
    buildOrchestrator: (epicIdentifier) => `@orchestrator execute epic ${quoteAgentArgument(epicIdentifier)}`,
    buildOrchestratorFeature: (identifier) => `@orchestrator execute feature ${quoteAgentArgument(identifier)}`,
    buildBugToErBridge: (caseId) => `@bug-to-er-bridge case ${String(caseId)}`,
  },
  claude: {
    hostLabel: "Claude Code",
    cliLabel: "Run in Claude Code (Terminal)",
    promptExplanation:
      "Paste this into the Claude Code prompt (the `claude` session) — not into a normal bash or zsh prompt.",
    launchCommand: "claude",
    verifyConnection: "/mcp",
    installSkillPacks: INSTALL_SKILL_PACKS_COMMAND,
    prerequisites: [
      {
        id: "claude-account",
        label: "Claude account",
        description:
          "Required to run Claude Code — a Claude Pro, Max, Team, or Enterprise plan (or a Claude Console account). You sign in through your browser the first time you run claude; no license request needed.",
      },
      {
        id: "enterprise-access",
        label: "GitHub Enterprise Access",
        description:
          "Required to access SpecTree repositories and install skill packs from GitHub Packages. Request through MyTech.",
      },
    ],
    buildRequestFormulator: (idea) => `/spectree:request-formulator ${quoteAgentArgument(idea)}`,
    buildImproveScore: (identifier) =>
      `/spectree:request-formulator improve ${quoteAgentArgument(identifier)}`,
    buildImproveEpic: (identifier) => `@agent-spectree:planner improve ${quoteAgentArgument(identifier)}`,
    buildPlanner: (description) => `@agent-spectree:planner ${quoteAgentArgument(description)}`,
    buildPlannerFromRequest: (title) => `@agent-spectree:planner plan from request ${quoteAgentArgument(title)}`,
    buildOrchestrator: (epicIdentifier) =>
      `/spectree:spectree-epic-execute ${normalizeWorkflowArgument(epicIdentifier)}`,
    buildOrchestratorFeature: (identifier) =>
      `@agent-spectree:orchestrator execute feature ${quoteAgentArgument(identifier)}`,
    buildBugToErBridge: (caseId) => `/spectree:bug-to-er ${String(caseId)}`,
  },
};

// ---------------------------------------------------------------------------
// Labels & prompt-context guidance (host-aware accessors + back-compat consts)
// ---------------------------------------------------------------------------

/** Label for the copyable launch/command box, for the given host. */
export function getAgentCliLabel(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].cliLabel;
}

/** Explains where a generated agent command should be pasted, for the given host. */
export function getAgentPromptExplanation(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].promptExplanation;
}

/** Display name of the host CLI (for prose/labels), for the given host. */
export function getHostLabel(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].hostLabel;
}

/** The launch command for the interactive host session, for the given host. */
export function getLaunchCommand(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].launchCommand;
}

/** The in-session connection-verification command, for the given host. */
export function getVerifyConnectionCommand(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].verifyConnection;
}

/** Account/license prerequisites the onboarding wizard shows, for the given host. */
export function getHostPrerequisites(
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): readonly HostPrerequisite[] {
  return HOST_GUIDANCE[host].prerequisites;
}

/** The skill-pack install command for the given host. */
export function getInstallSkillPacks(host: PreferredHost = DEFAULT_PREFERRED_HOST): string {
  return HOST_GUIDANCE[host].installSkillPacks;
}

/**
 * Host-correct in-session mention for a delegated subagent whose slug is the
 * same across hosts (e.g. `fogbugz-formulator`, `fogbugz-360-inspector`).
 * Copilot mentions a plain `@<slug>`; Claude mentions the plugin-namespaced
 * `@agent-spectree:<slug>` (the agent ships in the `spectree` Claude Code plugin).
 *
 * NOTE: request-formulator/bug-to-er-bridge run main-thread and planner/
 * orchestrator take host-specific arguments — use the dedicated builders for
 * those, not this helper.
 */
export function buildAgentMention(
  slug: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return host === "copilot" ? `@${slug}` : `@agent-spectree:${slug}`;
}

/**
 * @deprecated Prefer {@link getAgentCliLabel}(host). Default-host const kept so
 * not-yet-host-threaded consumers (A8b) compile + render coherently.
 */
export const AGENT_CLI_LABEL = HOST_GUIDANCE[DEFAULT_PREFERRED_HOST].cliLabel;

/**
 * @deprecated Prefer {@link getAgentPromptExplanation}(host). Default-host const
 * kept so not-yet-host-threaded consumers (A8b) compile + render coherently.
 */
export const AGENT_PROMPT_EXPLANATION = HOST_GUIDANCE[DEFAULT_PREFERRED_HOST].promptExplanation;

/** @deprecated Use {@link getAgentCliLabel}. Kept as a back-compat alias. */
export const COPILOT_CLI_LABEL = HOST_GUIDANCE[DEFAULT_PREFERRED_HOST].cliLabel;

/** @deprecated Use {@link getAgentPromptExplanation}. Kept as a back-compat alias. */
export const COPILOT_PROMPT_EXPLANATION = HOST_GUIDANCE[DEFAULT_PREFERRED_HOST].promptExplanation;

// ---------------------------------------------------------------------------
// Agent-command builders (host-aware; default DEFAULT_PREFERRED_HOST)
// ---------------------------------------------------------------------------

export function buildRequestFormulatorCommand(
  idea = "Describe the idea you want to propose",
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildRequestFormulator(idea);
}

export function buildImproveScoreCommand(
  identifier: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildImproveScore(identifier);
}

export function buildImproveEpicCommand(
  identifier: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildImproveEpic(identifier);
}

export function buildPlannerCommand(
  description = "Describe what you want to build",
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildPlanner(description);
}

export function buildPlannerFromRequestCommand(
  title: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildPlannerFromRequest(title);
}

export function buildOrchestratorCommand(
  epicIdentifier: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildOrchestrator(epicIdentifier);
}

export function buildOrchestratorFeatureCommand(
  identifier: string,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildOrchestratorFeature(identifier);
}

export function buildBugToErBridgeCommand(
  caseId: number,
  host: PreferredHost = DEFAULT_PREFERRED_HOST,
): string {
  return HOST_GUIDANCE[host].buildBugToErBridge(caseId);
}

// ---------------------------------------------------------------------------
// CLI Setup Wizard — prerequisites & platform-specific helpers (ENG-1398)
//
// @deprecated (A8b): the prerequisites + per-platform GitHub-Packages helpers
// below are consumed only by the legacy Help Center wizard
// (`components/onboarding/steps/*`). They are intentionally left on the Copilot
// flow so those wizard screens stay internally coherent until A8b migrates the
// wizard wholesale (drop the Copilot license prereq, add the Claude account,
// repoint the config path to the local-scoped `~/.claude.json` entry).
// ---------------------------------------------------------------------------

export const PREREQUISITES = {
  myTechUrl: "ttcitsm-ism.ivanticloud.com/Account/SelectRole?NoDefaultProvider=True",
  items: [
    {
      id: "copilot-license",
      label: "GitHub Copilot License",
      description: "Required for AI agent integration. Request through MyTech.",
    },
    {
      id: "enterprise-access",
      label: "GitHub Enterprise Access",
      description: "Required to access SpecTree repositories. Request through MyTech.",
    },
  ],
} as const;

export function getTokenCommand(platform: Platform): string {
  if (platform === "windows-cmd") {
    return "for /f %i in ('gh auth token') do npm config set //npm.pkg.github.com/:_authToken %i";
  }
  return "npm config set //npm.pkg.github.com/:_authToken $(gh auth token)";
}

export function getConfigPath(platform: Platform): string {
  if (platform === "windows-cmd" || platform === "windows-powershell") {
    return "%APPDATA%\\github-copilot\\config.json";
  }
  return "~/.config/github-copilot/config.json";
}
